import { PROFILE_ID } from '@apm/contracts';
import { useEffect, useSyncExternalStore } from 'react';

/**
 * 해시 라우팅 (7회차 결정 3, 22-B회차 메뉴 6개).
 *
 * 화면은 성격별 여섯 메뉴다 (2026-09-08 사용자 확정 5).
 *   실시간 / 요청(분포도 + 검색) / 통계 / 진단(오류·DB 감시·CPU 기록) / 알림 / 관리(저장소·외부 연동)
 * 진단·관리는 둘째 줄의 작은 탭으로 나뉘고 주소는 두 단계다(`#/diagnose/errors`).
 * 상세 둘(요청·CPU 기록)은 주소로 공유할 수 있어야 한다.
 *
 * 22-B회차 전의 주소(`#/xview` `#/search` `#/errors` `#/dbevents` `#/profiles` `#/storage`
 * `#/external`)는 북마크·알림 링크·지식 문서에 남아 있다. 전부 새 주소로 읽고, `useRoute`가
 * 주소창을 새 것으로 바꿔 둔다.
 *
 * 쿼리스트링을 싣는 라우트는 요청·통계·진단이다. 조건을 주소에 담아 그대로 보낼 수 있다(후보 #8).
 * 라우터 라이브러리는 넣지 않는다. 경로가 몇 개뿐이라 순수 함수 하나로 끝난다.
 * 해시를 쓰는 이유는 정적 파일 서빙(nginx)에서 history fallback 설정이 필요 없기 때문이다.
 */

/** 상세에서 "돌아가기"가 어디로 갈지 (콘솔 검토 C-5). 옛 `xview`·`search`는 `requests`로 읽는다 */
export type DetailOrigin = 'requests' | 'errors' | 'dbevents' | 'alerts' | 'stats';

/** 진단 메뉴의 하위 탭. 첫 탭은 오류다 */
export type DiagnoseSub = 'errors' | 'db' | 'cpu';
/** 관리 메뉴의 하위 탭. 관제 서버는 25회차에 셋째로 늘었다 */
export type AdminSub = 'storage' | 'external' | 'server';

export type Route =
  | { name: 'services' }
  | { name: 'live' }
  | { name: 'requests'; query?: string }
  | { name: 'stats'; query?: string }
  | { name: 'diagnose'; sub: DiagnoseSub; query?: string }
  | { name: 'alerts' }
  | { name: 'admin'; sub: AdminSub }
  | {
      name: 'detail';
      traceId: string;
      from?: DetailOrigin;
      back?: string;
      /** 같은 요청 번호에 여러 서비스 줄이 있을 때 상세가 읽을 줄. 전역 선택과는 별개다. */
      service?: string;
      /** 같은 서비스에도 줄이 둘일 때 상세가 읽을 실행 ID (D41-5). 주소의 `tx=` */
      txId?: string;
      /**
       * 그 요청의 시작 시각(epoch ms). 주소의 `at=` (59회차). 서버가 저장 파일을 그날 앞뒤만 연다.
       * 서버는 `tx=`와 함께일 때만 쓴다. 지금 싣는 화면은 없다. 없으면 거슬러 볼 기간 전체를 연다
       */
      at?: number;
    }
  | { name: 'profile'; id: string };

/** 위 줄의 탭. 상세는 탭이 아니다 */
export type TabName = 'live' | 'requests' | 'stats' | 'diagnose' | 'alerts' | 'admin';

export const TAB_NAMES: readonly TabName[] = [
  'live',
  'requests',
  'stats',
  'diagnose',
  'alerts',
  'admin',
];
export const DIAGNOSE_SUBS: readonly DiagnoseSub[] = ['errors', 'db', 'cpu'];
export const ADMIN_SUBS: readonly AdminSub[] = ['storage', 'external', 'server'];

const TRACE_ID = /^[0-9a-f]{32}$/;
const TX_ID = /^[0-9a-f]{16}$/;
/** 요청 시작 시각(epoch ms). 서버가 받는 범위 안인 15자리까지만 싣는다 (59회차) */
const EPOCH_MS = /^\d{1,15}$/;
const DIAGNOSE_SET = new Set<string>(DIAGNOSE_SUBS);
const ADMIN_SET = new Set<string>(ADMIN_SUBS);
const ORIGINS = new Set<string>(['requests', 'errors', 'dbevents', 'alerts', 'stats']);
/** 22-B회차 전의 출처 이름. 둘 다 이제 요청 화면이다 */
const OLD_ORIGINS: Record<string, DetailOrigin> = { xview: 'requests', search: 'requests' };

/** 22-B회차 전의 첫 세그먼트 → 새 자리. 조건은 그대로 따라간다 */
const OLD_HEADS: Record<string, Route> = {
  xview: { name: 'requests' },
  search: { name: 'requests' },
  errors: { name: 'diagnose', sub: 'errors' },
  dbevents: { name: 'diagnose', sub: 'db' },
  profiles: { name: 'diagnose', sub: 'cpu' },
  storage: { name: 'admin', sub: 'storage' },
  external: { name: 'admin', sub: 'external' },
};

function withQuery<T extends { name: 'requests' | 'stats' | 'diagnose' }>(
  route: T,
  query: string | undefined,
): T {
  return query === undefined || query === '' ? route : { ...route, query };
}

/** `#/tx/abc…` → 라우트. 모르는 주소는 첫 화면으로 보낸다 */
export function parseRoute(hash: string): Route {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const path = raw.startsWith('/') ? raw.slice(1) : raw;
  const queryAt = path.indexOf('?');
  const query = queryAt < 0 ? undefined : path.slice(queryAt + 1);
  const [head, second, third] = (queryAt < 0 ? path : path.slice(0, queryAt)).split('/');

  if (head === 'tx' && second !== undefined && TRACE_ID.test(second)) {
    const params = new URLSearchParams(query ?? '');
    const from = params.get('from');
    // 온 화면의 조건. 돌아가기가 그 조건을 그대로 되살린다 (23-A회차 리뷰 3)
    const back = params.get('back') ?? undefined;
    const service = params.get('svc') ?? undefined;
    const txId = params.get('tx') ?? '';
    const at = params.get('at') ?? '';
    const servicePart = {
      ...(service === undefined ? {} : { service }),
      // 모양이 아닌 값은 버린다. 서버가 400으로 거절할 주소를 만들지 않는다
      ...(TX_ID.test(txId) ? { txId } : {}),
      ...(EPOCH_MS.test(at) ? { at: Number(at) } : {}),
    };
    if (from !== null && ORIGINS.has(from)) {
      return { name: 'detail', traceId: second, from: from as DetailOrigin, back, ...servicePart };
    }
    const old = from === null ? undefined : OLD_ORIGINS[from];
    return old === undefined
      ? { name: 'detail', traceId: second, back, ...servicePart }
      : { name: 'detail', traceId: second, from: old, back, ...servicePart };
  }

  if (head === 'services') return { name: 'services' };
  if (head === 'live') return { name: 'live' };
  if (head === 'requests') return withQuery({ name: 'requests' }, query);
  if (head === 'stats') return withQuery({ name: 'stats' }, query);
  if (head === 'alerts') return { name: 'alerts' };

  if (head === 'diagnose') {
    // CPU 기록 상세는 하위 탭 아래에 붙는다(`#/diagnose/cpu/{id}`)
    if (second === 'cpu' && third !== undefined && third !== '') {
      return PROFILE_ID.test(third)
        ? { name: 'profile', id: third }
        : { name: 'diagnose', sub: 'cpu' };
    }
    const sub =
      second !== undefined && DIAGNOSE_SET.has(second) ? (second as DiagnoseSub) : 'errors';
    return withQuery({ name: 'diagnose', sub }, query);
  }
  if (head === 'admin') {
    const sub = second !== undefined && ADMIN_SET.has(second) ? (second as AdminSub) : 'storage';
    return { name: 'admin', sub };
  }

  // 22-B회차 전의 주소. 옛 CPU 기록 상세(`#/profiles/{id}`)도 여기서 읽는다
  if (head === 'profiles' && second !== undefined && second !== '') {
    return PROFILE_ID.test(second)
      ? { name: 'profile', id: second }
      : { name: 'diagnose', sub: 'cpu' };
  }
  const old = head === undefined ? undefined : OLD_HEADS[head];
  if (old !== undefined) {
    return old.name === 'requests' || old.name === 'diagnose' ? withQuery(old, query) : old;
  }
  return { name: 'services' };
}

export function routeHref(route: Route): string {
  switch (route.name) {
    case 'detail': {
      const params = new URLSearchParams();
      if (route.from !== undefined) params.set('from', route.from);
      // 온 화면의 조건을 통째로 싣는다. `URLSearchParams`가 알아서 감싼다
      if (route.back !== undefined && route.back !== '') params.set('back', route.back);
      if (route.service !== undefined && route.service !== '') params.set('svc', route.service);
      if (route.txId !== undefined && route.txId !== '') params.set('tx', route.txId);
      if (route.at !== undefined) params.set('at', String(route.at));
      const query = params.toString();
      return query === '' ? `#/tx/${route.traceId}` : `#/tx/${route.traceId}?${query}`;
    }
    case 'profile':
      return `#/diagnose/cpu/${route.id}`;
    case 'requests':
    case 'stats':
      return route.query === undefined || route.query === ''
        ? `#/${route.name}`
        : `#/${route.name}?${route.query}`;
    case 'diagnose':
      return route.query === undefined || route.query === ''
        ? `#/diagnose/${route.sub}`
        : `#/diagnose/${route.sub}?${route.query}`;
    case 'admin':
      return `#/admin/${route.sub}`;
    default:
      return `#/${route.name}`;
  }
}

/**
 * 상세의 출처 → 돌아갈 화면. `back`이 있으면 그 화면의 조건까지 되살린다 (23-A회차 리뷰 3).
 * 조건을 싣는 화면(요청·통계·진단)만 `back`을 쓴다
 */
export function originRoute(from: DetailOrigin, back?: string): Route {
  switch (from) {
    case 'requests':
      return { name: 'requests', query: back };
    case 'errors':
      return { name: 'diagnose', sub: 'errors', query: back };
    case 'dbevents':
      return { name: 'diagnose', sub: 'db', query: back };
    case 'alerts':
      return { name: 'alerts' };
    default:
      return { name: 'stats', query: back };
  }
}

/**
 * 지금 주소의 조건에 몇 개 키만 갈아 끼운다 (23-A회차 리뷰 3).
 *
 * 요청 화면은 조건 주인이 둘이다 — 위의 분포도와 아래의 검색. 한쪽이 제 조건만 쓰면서
 * 상대의 키를 지우지 않으려면 이 함수를 거쳐야 한다. 값이 `undefined`나 빈 문자열이면 그 키를 뺀다.
 */
export function mergeQuery(
  current: string | undefined,
  updates: Record<string, string | number | undefined>,
): string {
  const params = new URLSearchParams(current ?? '');
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined || value === '') params.delete(key);
    else params.set(key, String(value));
  }
  return params.toString();
}

/**
 * 주소의 조건에서 몇 키를 뺀 것 (44회차). 요청 화면의 검색 구획이 분포도 키를 빼고 제 조건만
 * 비교할 때 쓴다. 남는 키의 순서는 그대로다
 */
export function omitKeys(query: string | undefined, keys: readonly string[]): string {
  const params = new URLSearchParams(query ?? '');
  for (const key of keys) params.delete(key);
  return params.toString();
}

/**
 * 서비스를 고르는 첫 화면인지 (32회차).
 *
 * 이 화면에서는 아직 서비스가 정해지지 않아 헤더의 선택기·숫자와 화면 탭이 가리킬 대상이 없다.
 * 헤더는 제목과 실시간 연결 상태만 낸다.
 */
export function isDashboard(route: Route): boolean {
  return route.name === 'services';
}

/**
 * 화면 하나를 가리키는 열쇠 (44회차). 조건(쿼리)은 넣지 않는다 — 같은 화면에서 조건만 바뀐 것은
 * 다른 화면이 아니다. 화면 본문의 오류 경계가 이 값이 바뀔 때 저절로 풀린다
 */
export function pageKey(route: Route): string {
  switch (route.name) {
    case 'detail':
      return `detail/${route.traceId}/${route.service ?? ''}/${route.txId ?? ''}`;
    case 'profile':
      return `profile/${route.id}`;
    case 'diagnose':
    case 'admin':
      return `${route.name}/${route.sub}`;
    default:
      return route.name;
  }
}

/** 라우트가 어느 위 탭에 속하는지. 상세는 그것을 여는 화면의 탭에 붙인다 */
export function tabOf(route: Route): TabName | undefined {
  switch (route.name) {
    case 'services':
      return undefined;
    case 'detail':
      return route.from === undefined ? 'requests' : tabOf(originRoute(route.from));
    case 'profile':
      return 'diagnose';
    default:
      return route.name;
  }
}

/**
 * 주소를 새 모양으로. 옛 주소로 들어오면 `useRoute`가 이것으로 주소창을 바꾼다.
 * 이미 새 모양이면 그대로 돌려준다(빈 해시는 첫 화면)
 */
export function canonicalHash(hash: string): string {
  return routeHref(parseRoute(hash));
}

/** 주소를 바꾼다. `hashchange`가 뒤따라 구독자를 깨운다 */
export function navigate(route: Route): void {
  const href = routeHref(route);
  if (window.location.hash === href) return;
  window.location.hash = href;
}

/** 주소를 이력 한 칸 안에서 바꾸고 라우트 구독자를 깨운다. 초기 화면 교정에 쓴다. */
export function replaceRoute(route: Route): void {
  const href = routeHref(route);
  if (window.location.hash === href) return;
  window.history.replaceState(window.history.state, '', href);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

/**
 * 조건만 주소에 갈아 끼운다 (후보 #8).
 * 검색 결과를 그대로 붙여 넣을 수 있게 하되, 뒤로 가기 이력을 조건마다 남기지는 않는다.
 */
export function replaceQuery(route: Route): void {
  const href = routeHref(route);
  if (window.location.hash === href) return;
  window.history.replaceState(null, '', href);
  // 라우트 구독자(`hashchange`)는 깨우지 않는다. 조건만 보는 곳(`useCurrentQuery`)만 깨운다 (R62-2)
  window.dispatchEvent(new Event(QUERY_EVENT));
}

/** `replaceQuery`가 조건을 갈아 끼웠을 때 내는 사건 이름 */
const QUERY_EVENT = 'apm:query';

function subscribeQuery(listener: () => void): () => void {
  window.addEventListener('hashchange', listener);
  window.addEventListener(QUERY_EVENT, listener);
  return () => {
    window.removeEventListener('hashchange', listener);
    window.removeEventListener(QUERY_EVENT, listener);
  };
}

function serverQuery(): string | undefined {
  return undefined;
}

/**
 * 지금 주소의 조건을 구독한다 (R62-2). `memo` 표가 상세 링크의 `back`을 렌더 중에 `currentQuery()`로
 * 읽으면 조건이 바뀌어도 다시 그리지 않아 옛 조건이 남았다. 부모가 이 값을 표에 넘기면 조건이 바뀔
 * 때만 표가 다시 그려진다. 실시간 반영으로는 바뀌지 않는다
 */
export function useCurrentQuery(): string | undefined {
  return useSyncExternalStore(subscribeQuery, currentQuery, serverQuery);
}

/**
 * 지금 주소에 실린 조건 (23-A회차 리뷰 3). 상세로 갈 때 `back`에 그대로 싣고,
 * 조건 주인이 둘인 화면이 서로의 키를 지우지 않게 `mergeQuery`의 바탕으로 쓴다
 */
export function currentQuery(): string | undefined {
  const query = window.location.hash.split('?')[1];
  return query === undefined || query === '' ? undefined : query;
}

function subscribe(listener: () => void): () => void {
  window.addEventListener('hashchange', listener);
  return () => window.removeEventListener('hashchange', listener);
}

function currentHash(): string {
  return window.location.hash;
}

/** 서버 렌더는 없지만 `useSyncExternalStore`가 요구한다 */
function serverHash(): string {
  return '';
}

export function useRoute(): Route {
  const hash = useSyncExternalStore(subscribe, currentHash, serverHash);
  // 옛 주소·빈 해시는 새 주소로 바꿔 둔다. 이력을 남기지 않아 뒤로 가기가 자연스럽다
  useEffect(() => {
    const canonical = canonicalHash(hash);
    if (hash !== canonical) window.location.replace(canonical);
  }, [hash]);
  return parseRoute(hash);
}
